import { apiBase, authHeaders, resolveOrgId } from '../utils/authHeaders';

export function detailOf(data, fallback = '') {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  if (d && typeof d === 'object' && d.message) return d.message;   // structured refusal, e.g. step_up_required
  return fallback;
}

/** JSON call against the API with the session headers; throws Error(detail) on a non-2xx. */
export async function apiJson(path, { method = 'GET', body, headers } = {}) {
  const res = await fetch(`${apiBase()}${path}`, {
    method,
    headers: { ...authHeaders({ orgId: resolveOrgId(), json: true }), ...(headers || {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const err = new Error(detailOf(data, `Request failed (${res.status})`));
    err.status = res.status;
    err.code = data && data.detail && typeof data.detail === 'object' ? data.detail.code : undefined;
    throw err;
  }
  return data;
}

export async function uploadInvoiceFiles(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f, f.name || 'upload.bin');
  const headers = authHeaders({ orgId: resolveOrgId() });
  delete headers['Content-Type'];
  const res = await fetch(`${apiBase()}/api/documents/bulk`, { method: 'POST', headers, body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailOf(data, `Upload failed (${res.status})`));
  return data;
}

/** Where uploaded files are right now: queued behind N, being read, failed or gone (GET /api/documents/progress). */
export const documentProgress = (ids) =>
  apiJson(`/api/documents/progress?ids=${ids.map(encodeURIComponent).join(',')}`);
export const resolveTask = (taskId, body) => apiJson(`/api/review/${taskId}/resolve`, { method: 'POST', body });
export const searchCandidates = (q) => apiJson(`/api/review/candidates?q=${encodeURIComponent(q)}`);
export const createItem = (name, unit, documentId) =>
  apiJson('/api/review/items', { method: 'POST', body: { name, unit, document_id: documentId } });
export const getInvoiceDetail = (id) => apiJson(`/api/review/invoices/${id}`);
export const getItem = (id) => apiJson(`/api/items/${encodeURIComponent(id)}`);
export const invoiceCommand = (id, action, body) =>
  apiJson(`/api/review/invoices/${id}/${action}`, { method: 'POST', body });
export const correctInvoice = (id, body) => apiJson(`/api/review/invoices/${id}/correct`, { method: 'POST', body });
const stepUpHeader = (token) => (token ? { 'X-Step-Up-Token': token } : undefined);
export const removeUpload = (id, stepUpToken) =>
  apiJson(`/api/review/invoices/${id}`, { method: 'DELETE', headers: stepUpHeader(stepUpToken) });
export const demoResetStatus = () => apiJson('/api/review/demo-reset');
export const demoReset = (stepUpToken) =>
  apiJson('/api/review/demo-reset', { method: 'POST', headers: stepUpHeader(stepUpToken) });
export const demoFullReset = (stepUpToken) =>
  apiJson('/api/review/demo-reset/full?mode=fast', { method: 'POST', headers: stepUpHeader(stepUpToken) });
/** Second-level authorisation (backend/step_up.py): how this person confirms, and a one-use token for one action. */
export const stepUpMethod = () => apiJson('/api/auth/step-up/method');
export const stepUp = (action, { code, password } = {}) =>
  apiJson('/api/auth/step-up', { method: 'POST', body: { action, code, password } });
/** Email intake: what is happening to the invoice mailbox, and what a person can do about a problem email. */
export const getMailLedger = (days = 7) => apiJson(`/api/intake/mail-ledger?days=${days}`);
export const retryMailProblem = (key) => apiJson(`/api/intake/mail-ledger/${key}/retry`, { method: 'POST' });
export const dismissMailProblem = (key) => apiJson(`/api/intake/mail-ledger/${key}/dismiss`, { method: 'POST' });
/** The business's own invoice addresses: email to (or read from) them comes here, no "+tag" needed. */
export const getIntakeAddresses = () => apiJson('/api/intake/mail-ledger/addresses');
export const setIntakeAddresses = (addresses) =>
  apiJson('/api/intake/mail-ledger/addresses', { method: 'PUT', body: { addresses } });
